'use client';

import { useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { storage } from '@/lib/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { Upload, Loader2, X, Camera } from 'lucide-react';
import Image from 'next/image';
import { useSeasonalLogo } from '@/hooks/useSeasonalLogo';

export function ProfilePictureUpload() {
  const { user, userProfile, updateProfile } = useAuth();
  const { themeColor } = useSeasonalLogo();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!user || !fileInputRef.current?.files?.[0]) return;

    const file = fileInputRef.current.files[0];
    setUploading(true);

    try {
      // Create a reference to the file location
      const fileExtension = file.name.split('.').pop();
      const fileName = `profile-pictures/${user.uid}.${fileExtension}`;
      const storageRef = ref(storage, fileName);

      // Upload the file
      await uploadBytes(storageRef, file);

      // Get the download URL
      const downloadURL = await getDownloadURL(storageRef);

      // Update user profile with new photo URL and mark as custom
      await updateProfile({ photoURL: downloadURL, hasCustomPhoto: true });

      toast.success('Profile picture updated successfully!');
      setPreview(null);

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      toast.error('Failed to upload profile picture');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!user || !userProfile?.photoURL) return;

    setUploading(true);
    try {
      // Delete the old photo from storage
      try {
        const fileRef = ref(storage, userProfile.photoURL);
        await deleteObject(fileRef);
      } catch (error) {
        // Photo might not exist in our storage (could be Google profile pic)
      }

      // Remove photo URL from profile and clear custom photo flag
      await updateProfile({ photoURL: null as any, hasCustomPhoto: false });

      toast.success('Profile picture removed');
    } catch (error) {
      toast.error('Failed to remove profile picture');
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const currentPhotoURL = preview || userProfile?.photoURL || user?.photoURL;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-6">
        {/* Profile Picture Display */}
        <div className="relative">
          <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-gray-700 bg-gray-800">
            {currentPhotoURL ? (
              <Image
                src={currentPhotoURL}
                alt="Profile"
                width={96}
                height={96}
                className="w-full h-full object-cover"
              />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center"
                style={{
                  background: `linear-gradient(to bottom right, ${themeColor}, ${themeColor}dd)`
                }}
              >
                <span className="text-3xl font-bold text-white">
                  {user?.email?.charAt(0).toUpperCase() || '?'}
                </span>
              </div>
            )}
          </div>

          {/* Camera icon overlay */}
          {!preview && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="absolute bottom-0 right-0 p-2 rounded-full text-white border-2 border-gray-900 transition-all disabled:opacity-50"
              style={{ backgroundColor: themeColor }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              <Camera className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Upload Controls */}
        <div className="flex-1 space-y-3 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />

            {!preview ? (
              <>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  variant="outline"
                  className="border-gray-700 hover:bg-gray-800 flex-shrink-0"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Choose Photo
                </Button>

                {currentPhotoURL && (
                  <Button
                    onClick={handleRemove}
                    disabled={uploading}
                    variant="outline"
                    className="border-red-800 text-red-400 hover:bg-red-950 flex-shrink-0"
                  >
                    {uploading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <X className="h-4 w-4 mr-2" />
                    )}
                    Remove
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="bg-red-600 hover:bg-red-700 flex-shrink-0"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleCancel}
                  disabled={uploading}
                  variant="outline"
                  className="border-gray-700 hover:bg-gray-800 flex-shrink-0"
                >
                  Cancel
                </Button>
              </>
            )}
          </div>

          <p className="text-xs text-gray-400">
            {preview
              ? 'Click Upload to save your new profile picture'
              : 'JPG, PNG or GIF. Max size 5MB.'
            }
          </p>
        </div>
      </div>
    </div>
  );
}
